/**
 * Dashboard do Sistema MEDICARE
 * Página principal com visão geral do sistema
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Card,
  CardBody,
  VStack,
  HStack,
  Text,
  Icon,
  useColorModeValue,
  Progress,
  Badge,
  Flex,
  Avatar,
  AvatarGroup,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  IconButton,
} from '@chakra-ui/react';
import {
  FiUsers,
  FiCalendar,
  FiActivity,
  FiTrendingUp,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardService, EstatisticasDashboard, EvolucaoData, ConsultasRecentesPaginadas } from '../../services/dashboard';
import EvolucaoChart from '../../components/charts/EvolucaoChart';

const mockRecentConsultas = [
  {
    id: 1,
    paciente: 'Maria Silva',
    medico: 'Dr. João Santos',
    horario: '14:30',
    status: 'em_andamento',
    tipo: 'Consulta Geral',
  },
  {
    id: 2,
    paciente: 'Carlos Oliveira',
    medico: 'Dra. Ana Costa',
    horario: '15:00',
    status: 'agendada',
    tipo: 'Cardiologia',
  },
  {
    id: 3,
    paciente: 'Fernanda Lima',
    medico: 'Dr. Pedro Alves',
    horario: '15:30',
    status: 'concluida',
    tipo: 'Dermatologia',
  },
];

const mockMedicos = [
  { nome: 'Dr. João Santos', especialidade: 'Clínico Geral', avatar: '' },
  { nome: 'Dra. Ana Costa', especialidade: 'Cardiologia', avatar: '' },
  { nome: 'Dr. Pedro Alves', especialidade: 'Dermatologia', avatar: '' },
  { nome: 'Dra. Maria Souza', especialidade: 'Pediatria', avatar: '' },
];

export const DashboardPage: React.FC = () => {
  const { usuario } = useAuth();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const smallTextColor = useColorModeValue('gray.600', 'gray.400');

  // Estados para dados do dashboard
  const [estatisticas, setEstatisticas] = useState<EstatisticasDashboard | null>(null);
  const [evolucaoData, setEvolucaoData] = useState<EvolucaoData[]>([]);
  const [consultasRecentes, setConsultasRecentes] = useState<ConsultasRecentesPaginadas | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingEvolucao, setLoadingEvolucao] = useState(true);
  const [loadingConsultas, setLoadingConsultas] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginaAtual, setPaginaAtual] = useState(1);

  const carregarConsultasRecentes = async () => {
    try {
      setLoadingConsultas(true);
      const response = await DashboardService.obterConsultasRecentes(paginaAtual, 5);
      
      if (response.success && response.data) {
        setConsultasRecentes(response.data);
      } else {
        console.error('Erro ao carregar consultas recentes:', response.message);
      }
    } catch (error) {
      console.error('Erro ao carregar consultas recentes:', error);
    } finally {
      setLoadingConsultas(false);
    }
  };

  // Carregar dados do dashboard
  useEffect(() => {
    const carregarDados = async () => {
      try {
        setLoading(true);
        setLoadingEvolucao(true);
        setError(null);
        
        // Carregar estatísticas e dados de evolução em paralelo
        const [estatisticasResponse, evolucaoResponse] = await Promise.all([
          DashboardService.obterEstatisticas(),
          DashboardService.obterEvolucao()
        ]);
        
        if (estatisticasResponse.success && estatisticasResponse.data) {
          setEstatisticas(estatisticasResponse.data);
        } else {
          setError(estatisticasResponse.error?.message || 'Erro ao carregar estatísticas');
        }

        if (evolucaoResponse.success && evolucaoResponse.data) {
          setEvolucaoData(evolucaoResponse.data);
        }
        
      } catch (err: any) {
        console.error('Erro ao carregar dados do dashboard:', err);
        setError('Erro ao carregar dados do dashboard');
      } finally {
        setLoading(false);
        setLoadingEvolucao(false);
      }
    };

    carregarDados();
    carregarConsultasRecentes();
  }, []);

  useEffect(() => {
    carregarConsultasRecentes();
  }, [paginaAtual]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'em_andamento':
        return 'blue';
      case 'agendada':
        return 'yellow';
      case 'concluida':
        return 'green';
      case 'cancelada':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'em_andamento':
        return 'Em Andamento';
      case 'agendada':
        return 'Agendada';
      case 'concluida':
        return 'Concluída';
      case 'cancelada':
        return 'Cancelada';
      default:
        return status;
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  // Mostrar loading
  if (loading) {
    return (
      <Container maxW="7xl" py={6}>
        <VStack spacing={8} align="center" justify="center" minH="400px">
          <Spinner size="xl" color="medicare.500" />
          <Text>Carregando estatísticas do dashboard...</Text>
        </VStack>
      </Container>
    );
  }

  // Mostrar erro
  if (error) {
    return (
      <Container maxW="7xl" py={6}>
        <Alert status="error">
          <AlertIcon />
          <Box>
            <AlertTitle>Erro ao carregar dashboard!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Box>
        </Alert>
      </Container>
    );
  }

  // Se não há dados, mostrar mensagem
  if (!estatisticas) {
    return (
      <Container maxW="7xl" py={6}>
        <Alert status="warning">
          <AlertIcon />
          <Box>
            <AlertTitle>Nenhum dado encontrado!</AlertTitle>
            <AlertDescription>
              Não foi possível carregar as estatísticas do dashboard.
            </AlertDescription>
          </Box>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="7xl" py={6}>
      <VStack spacing={8} align="stretch">
        {/* Header de boas-vindas */}
        <Box>
          <Heading size="lg" mb={2}>
            {getGreeting()}, {usuario?.nome}!
          </Heading>
          <Text color={textColor}>
            Aqui está um resumo das atividades do sistema MEDICARE hoje.
          </Text>
        </Box>

        {/* Cards de estatísticas */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          <Card bg={cardBg}>
            <CardBody>
              <Stat>
                <Flex justify="space-between" align="center">
                  <Box>
                    <StatLabel>Total de Usuários</StatLabel>
                    <StatNumber>{estatisticas.totalUsuarios}</StatNumber>
                    <StatHelpText>
                      <StatArrow type="increase" />
                      Usuários cadastrados
                    </StatHelpText>
                  </Box>
                  <Icon as={FiUsers} boxSize={8} color="medicare.500" />
                </Flex>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg}>
            <CardBody>
              <Stat>
                <Flex justify="space-between" align="center">
                  <Box>
                    <StatLabel>Consultas Hoje</StatLabel>
                    <StatNumber>{estatisticas.consultasHoje}</StatNumber>
                    <StatHelpText>
                      <StatArrow type="increase" />
                      Consultas realizadas hoje
                    </StatHelpText>
                  </Box>
                  <Icon as={FiCalendar} boxSize={8} color="blue.500" />
                </Flex>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg}>
            <CardBody>
              <Stat>
                <Flex justify="space-between" align="center">
                  <Box>
                    <StatLabel>Total de Consultas</StatLabel>
                    <StatNumber>{estatisticas.totalConsultas}</StatNumber>
                    <StatHelpText>
                      <StatArrow type="increase" />
                      Consultas efetuadas
                    </StatHelpText>
                  </Box>
                  <Icon as={FiActivity} boxSize={8} color="green.500" />
                </Flex>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Gráfico de Evolução */}
        <EvolucaoChart data={evolucaoData} loading={loadingEvolucao} />

        {/* Grid com informações detalhadas */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {/* Consultas recentes */}
          <Card bg={cardBg}>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Flex justify="space-between" align="center">
                  <Heading size="md">Consultas Recentes</Heading>
                  {consultasRecentes && consultasRecentes.paginacao.total_paginas > 1 && (
                    <HStack>
                      <Button
                         size="sm"
                         variant="ghost"
                         isDisabled={paginaAtual === 1}
                         onClick={() => setPaginaAtual(prev => Math.max(1, prev - 1))}
                       >
                         ‹
                       </Button>
                       <Text fontSize="sm">
                         {paginaAtual} de {consultasRecentes.paginacao.total_paginas}
                       </Text>
                       <Button
                         size="sm"
                         variant="ghost"
                         isDisabled={paginaAtual === consultasRecentes.paginacao.total_paginas}
                         onClick={() => setPaginaAtual(prev => Math.min(consultasRecentes.paginacao.total_paginas, prev + 1))}
                       >
                         ›
                       </Button>
                    </HStack>
                  )}
                </Flex>
                
                {loadingConsultas ? (
                  <Flex justify="center" py={4}>
                    <Spinner />
                  </Flex>
                ) : consultasRecentes && consultasRecentes.consultas.length > 0 ? (
                  consultasRecentes.consultas.map((consulta) => (
                    <Box
                      key={consulta.id}
                      p={4}
                      border="1px"
                      borderColor={borderColor}
                      borderRadius="md"
                    >
                      <Flex justify="space-between" align="center" mb={2}>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">{consulta.paciente}</Text>
                          <Text fontSize="sm" color={smallTextColor}>
                            {consulta.medico} • {consulta.tipo}
                          </Text>
                        </VStack>
                        <VStack align="end" spacing={1}>
                          <Badge colorScheme={getStatusColor(consulta.status)}>
                            {getStatusLabel(consulta.status)}
                          </Badge>
                          <Text fontSize="sm">
                            {new Date(consulta.data).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })} às {new Date(consulta.data).toLocaleTimeString('pt-BR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Text>
                        </VStack>
                      </Flex>
                    </Box>
                  ))
                ) : (
                  <Text color={smallTextColor} textAlign="center" py={4}>
                    Nenhuma consulta recente encontrada
                  </Text>
                )}
                
                {consultasRecentes && consultasRecentes.paginacao.total_itens > 0 && (
                  <Text fontSize="xs" color={smallTextColor} textAlign="center">
                    Total: {consultasRecentes.paginacao.total_itens} consultas
                  </Text>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Equipe médica */}
          <Card bg={cardBg}>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Flex justify="space-between" align="center">
                  <Heading size="md">Equipe Médica</Heading>
                  <AvatarGroup size="sm" max={3}>
                    {estatisticas?.medicosAtivos && estatisticas.medicosAtivos.slice(0, 3).map((medico) => (
                      <Avatar key={medico.id} name={medico.nome} />
                    ))}
                  </AvatarGroup>
                </Flex>
                
                {estatisticas?.medicosAtivos && estatisticas.medicosAtivos.length > 0 ? (
                  estatisticas.medicosAtivos.map((medico) => (
                    <Flex key={medico.id} align="center" justify="space-between">
                      <HStack>
                        <Avatar size="sm" name={medico.nome} />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium" fontSize="sm">
                            {medico.nome}
                          </Text>
                          <Text fontSize="xs" color={smallTextColor}>
                            {medico.especialidade}
                          </Text>
                        </VStack>
                      </HStack>
                      <HStack>
                        <Text fontSize="xs" color={smallTextColor}>
                          {medico.consultasHoje} consultas efetuadas
                        </Text>
                        <Icon 
                          as={FiCheckCircle} 
                          color={medico.status === 'online' ? 'green.500' : 
                                 medico.status === 'ocupado' ? 'yellow.500' : 'gray.500'} 
                        />
                      </HStack>
                    </Flex>
                  ))
                ) : (
                  <Text color={smallTextColor} textAlign="center" py={4}>
                    Nenhum médico ativo encontrado
                  </Text>
                )}
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Indicadores de performance */}
        <Card bg={cardBg}>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Heading size="md">Indicadores de Performance</Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <Box>
                  <Text fontSize="sm" mb={2}>Taxa de Ocupação</Text>
                  <Progress value={85} colorScheme="medicare" size="lg" />
                  <Text fontSize="xs" color={smallTextColor} mt={1}>
                    85% das consultas agendadas
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" mb={2}>Tempo Médio de Consulta</Text>
                  <Progress value={70} colorScheme="blue" size="lg" />
                  <Text fontSize="xs" color={smallTextColor} mt={1}>
                    28 minutos em média
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" mb={2}>Satisfação do Paciente</Text>
                  <Progress value={96} colorScheme="green" size="lg" />
                  <Text fontSize="xs" color={smallTextColor} mt={1}>
                    4.8/5 estrelas
                  </Text>
                </Box>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default DashboardPage;